/**
 * Use Revised Text Hook
 *
 * Manages the accept/ignore workflow for issues and computes
 * the revised text based on accepted suggestions.
 */

import { useState, useCallback, useMemo } from 'react';
import type { InlineIssue, IssueStatus, ViewMode } from '../types';
import { getRevisedText, countByStatus, hasChanges } from '../utils/highlightText';

interface UseRevisedTextOptions {
  /** Original message text */
  message: string;
  /** Initial issues array */
  initialIssues: InlineIssue[];
}

interface UseRevisedTextReturn {
  /** Current issues with status */
  issues: InlineIssue[];
  /** Current view mode */
  viewMode: ViewMode;
  /** Revised text with accepted replacements */
  revisedText: string;
  /** Whether there are any changes */
  hasChanges: boolean;
  /** Count of issues by status */
  statusCounts: Record<IssueStatus, number>;
  /** Set view mode */
  setViewMode: (mode: ViewMode) => void;
  /** Update issue status */
  updateIssueStatus: (issueId: number, status: IssueStatus) => void;
  /** Accept all pending issues */
  acceptAll: () => void;
  /** Reset all issues to pending */
  resetAll: () => void;
}

/**
 * Hook for managing revised text computation and issue workflow
 */
export function useRevisedText({
  message,
  initialIssues,
}: UseRevisedTextOptions): UseRevisedTextReturn {
  const [issues, setIssues] = useState<InlineIssue[]>(initialIssues);
  const [viewMode, setViewMode] = useState<ViewMode>('original');

  // Compute revised text
  const revisedText = useMemo(
    () => getRevisedText(message, issues),
    [message, issues]
  );

  // Compute status counts
  const statusCounts = useMemo(() => countByStatus(issues), [issues]);

  // Check if there are changes
  const hasIssueChanges = useMemo(() => hasChanges(issues), [issues]);

  // Update single issue status
  const updateIssueStatus = useCallback(
    (issueId: number, status: IssueStatus) => {
      setIssues((prev) =>
        prev.map((issue) =>
          issue.id === issueId ? { ...issue, status } : issue
        )
      );
    },
    []
  );

  // Accept all pending issues
  const acceptAll = useCallback(() => {
    setIssues((prev) =>
      prev.map((issue) =>
        issue.status === 'pending' ? { ...issue, status: 'accepted' } : issue
      )
    );
  }, []);

  // Reset all issues to pending
  const resetAll = useCallback(() => {
    setIssues((prev) =>
      prev.map((issue) => ({ ...issue, status: 'pending' }))
    );
    setViewMode('original');
  }, []);

  return {
    issues,
    viewMode,
    revisedText,
    hasChanges: hasIssueChanges,
    statusCounts,
    setViewMode,
    updateIssueStatus,
    acceptAll,
    resetAll,
  };
}

export default useRevisedText;
